import type React from "react";
import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";

type PendingActionContextType = {
    /** 進行中の処理が 1 つ以上あるか */
    isPending: boolean;
    /** 非同期処理を「処理中」として数えながら実行する。成功・失敗を問わず終了時に数え戻す */
    runWithPending: <T>(action: () => Promise<T>) => Promise<T>;
};

// Provider 外 (単体テスト等) では数えずに素通しする。処理そのものは阻害しない。
const passthrough: PendingActionContextType = {
    isPending: false,
    runWithPending: (action) => action(),
};

export const PendingActionContext = createContext<PendingActionContextType>(passthrough);

// clientAction を経由せず hooks から直接 Hono RPC を叩く処理 (完了ロック / 公開状態変更 / チケット紐付け等) が、
// 進行中であることをアプリ全体へ知らせるための Context。
// clientAction 経由の送信は useNavigationPending が拾うので、両者を OR したものが「POST 通信中」になる。
export const usePendingAction = () => useContext(PendingActionContext);

export const PendingActionProvider = ({ children }: { children: React.ReactNode }) => {
    const [count, setCount] = useState(0);
    // 同時に複数走っても、全部終わるまで isPending を保つためカウンタで持つ
    const countRef = useRef(0);

    const runWithPending = useCallback(async <T,>(action: () => Promise<T>): Promise<T> => {
        countRef.current += 1;
        setCount(countRef.current);
        try {
            return await action();
        } finally {
            countRef.current -= 1;
            setCount(countRef.current);
        }
    }, []);

    const value = useMemo(() => ({ isPending: count > 0, runWithPending }), [count, runWithPending]);

    return <PendingActionContext.Provider value={value}>{children}</PendingActionContext.Provider>;
};
